import {
  Edit,
  SimpleForm,
  TextInput,
  useRecordContext,
  useRedirect,
  useNotify,
  Toolbar,
  DeleteButton,
  SaveButton,
  required,
} from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { CustomActions } from '../../components';
import { usersProvider } from '../../providers';
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import _ from "lodash";

const CustomToolbar = ({originRoles, selectedRoles}) => {
  const record = useRecordContext();
  const redirect = useRedirect(); // Hook to redirect to another page
  const theme = useTheme(); // Access the theme
  const notify = useNotify();
  const backgroundColor = theme.palette.background.paper; // Default background color
  const [isLoading, setIsLoading] = useState(false);

  const isDifferent = !_.isEqual(originRoles, selectedRoles);

  const handleCancel = () => {
    redirect('list', 'cognitoUsers');
  };

  const handleSuccessUpdate = async (res) => {
    setIsLoading(true);
    try {
      if (isDifferent) {
        const addRoles = _.differenceWith(selectedRoles, originRoles, (a, b) => a.GroupName === b.GroupName);
        const removeRoles = _.differenceWith(originRoles, selectedRoles, (a, b) => a.GroupName === b.GroupName);
        const promises = [];
        addRoles.forEach((aR) => promises.push(usersProvider("POST", "/addUserToGroup", {
          username: record.id,
          groupname: aR.GroupName,
        })));
        removeRoles.forEach((rR) => promises.push(usersProvider("POST", "/removeUserFromGroup", {
          username: record.id,
          groupname: rR.GroupName,
        })));
        await Promise.all( promises );
      }
      await usersProvider("PUT", "/updateUser", {
        userid: res.id,
        username: res.name,
        email: res.email,
        groupname: selectedRoles.map((sr) => sr.GroupName),
      })
      notify('User updated successfully', { type: 'success' });
      redirect('list', 'cognitoUsers');
      window.location.reload();
    } catch (error) {
      console.error('Error updating User:', error);
      notify('Error updating User', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccessDelete = async (res) => {
    setIsLoading(true);
    try {
      await usersProvider("DELETE", "/deleteUser", {
        username: res.id
      });
      notify('User deleted successfully', { type: 'success' });
      redirect('list', 'cognitoUsers');
    } catch (error) {
      console.error('Error deleting User:', error);
      notify('Error deleting User', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Toolbar
      sx={{
        backgroundColor, // Set the background color
      }}
    >
      <Box display="flex" justifyContent="space-between" width="100%">
        <DeleteButton
          type='button'
          size='small'
          label='Delete'
          sx={{textTransform: 'none', minWidth: '95px'}}
          mutationOptions={{ onSuccess: handleSuccessDelete }}
          disabled={isLoading}
        /> 
        <Box>
          <Button
            variant="text"
            onClick={handleCancel}
            sx={{
              color: theme.palette.common.white, // Optional: primary color for cancel button text
              borderColor: theme.palette.primary.main, // Optional: primary color for cancel button border
              marginRight: theme.spacing(1),
              textTransform: 'none',
              minWidth: '95px',
            }}
            size='small'
            disabled={isLoading}
          >
            Cancel
          </Button>
          <SaveButton
            alwaysEnable={isDifferent && !isLoading}
            type='button'
            label='Update'
            size='small'
            icon={null}
            sx={{textTransform: 'none', minWidth: '95px'}}
            mutationOptions={{ onSuccess: handleSuccessUpdate }}
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Toolbar>
  );
};

const RoleList = ({onChange}) => {
  const record = useRecordContext();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [originRoles, setOriginRoles] = useState([]);

  useEffect(() => {
    const getRoles = async () => {
      setLoading(true);
      try {
        const allRoles = await usersProvider("GET", "/listGroups", {})
        const selected = await usersProvider("GET", "/listGroupsForUser", { username: record.id})
        setRoles(allRoles.Groups || []);
        setOriginRoles(selected.Groups || []);
        setSelectedRoles(selected.Groups.map((sr) => sr.GroupName)[0])
        onChange(selected.Groups || [], selected.Groups || []);
      } catch (error) {
        console.error("RoleList Error:", error);
      } finally {
        setLoading(false);
      }
    };

    getRoles();
  }, [record]);

  const handleChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedRoles(selectedValues);
    onChange(originRoles, roles.filter((role) => selectedValues.includes(role.GroupName)));
  };

  return (
    <FormControl fullWidth>
      <InputLabel id="role-select-label">Select Role</InputLabel>
      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" height={50}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Select
          labelId="role-select-label"
          value={selectedRoles}
          onChange={handleChange}
          renderValue={(selected) => `${selected}`}
          style={{ maxHeight: 300, overflowY: "auto" }}
        >
          {roles.map((role) => (
            <MenuItem key={role.GroupName} value={role.GroupName}>
              <ListItemText primary={role.GroupName} />
            </MenuItem>
          ))}
        </Select>
      )}
    </FormControl>
  )
}

const EditUser = () => {
  const [originRoles, setoriginRoles] = useState([]);
  const [changedRoles, setChangedRoles] = useState([]);

  return (
    <Card variant="elevation" sx={{ width: '50%' }}>
      <CardContent>
        <Edit
          title="Edit User"
          actions={<CustomActions title="Edit User" type="edit" />}
        >
          <SimpleForm toolbar={<CustomToolbar originRoles={originRoles} selectedRoles={changedRoles} />}>
            <Typography variant="subtitle2">User Details</Typography>
            <TextInput source="name" label="NAME" fullWidth validate={[required('Name is required')]} />
            <TextInput source="email" label="EMAIL" fullWidth InputProps={{ readOnly: true }}/>

            <Typography variant="subtitle2" gutterBottom>
              Role
            </Typography>
            <RoleList
              onChange={(origin, changed) => {
                setoriginRoles(origin);
                setChangedRoles(changed);
              }}
            />
          </SimpleForm>
        </Edit>
      </CardContent>
    </Card>
  );
};

export default EditUser;
export { EditUser };